import React, { useState, useMemo, useEffect, useRef } from 'react';
import T from '../styles/theme';
import {Badge, Avatar, ProgressBar, Btn, Select } from '../components/UI';
import Icon from '../components/Icon';
import { useStore } from '../data/store';
import { useAuth } from '../data/auth';
import { useToast } from '../components/Toast';
import { makeCall, hangUp, onCallEvent, offCallEvent, getStatus } from '../data/twilio';
import { logCall as logCallToDb } from '../data/callLogger';
import supabase from '../data/supabase';
import ContactForm from '../components/contacts/ContactForm';
import QuickActionPanel from '../components/QuickActionPanel';
import ChangeHistorySection from '../components/ChangeHistorySection';
import { EditActivityModal } from '../components/EditActivityModal';
/* No direct STAGES import - read from store */

const cfDateTime = (d) => { if (!d) return "—"; const dt = new Date(d); const m = String(dt.getMonth()+1).padStart(2,"0"); const dy = String(dt.getDate()).padStart(2,"0"); const y = String(dt.getFullYear()).slice(-2); let h = dt.getHours(); const ap = h >= 12 ? "PM" : "AM"; h = h % 12 || 12; const mn = String(dt.getMinutes()).padStart(2,"0"); return m+"/"+dy+"/"+y+" "+h+":"+mn+" "+ap; };

// Local replica of store.js's dbActivityToLocal — kept inline because the
// store-side function isn't exported, and this file's per-contact direct
// query bypasses the 200-row global activities cache (which is email-skewed
// by server-side automation + bulk imports). The mapping shape must match
// what the rest of ContactRecord already consumes from store.activities so
// merge/dedup in filteredActs works cleanly.
const dbActivityToLocal = (row) => ({
  id: row.id, type: row.type, title: row.title,
  notes: row.notes || "",
  date: row.created_at ? new Date(row.created_at).toISOString().slice(0, 16).replace("T", " ") : "",
  contactId: row.contact_id || "", dealId: row.deal_id || "",
  companyId: row.company_id || null,
  by: row.user_id || "", duration: row.duration || "",
  properties: row.properties || {},
  ...Object.fromEntries(Object.entries(row.properties || {}).filter(([k]) => !["id","type","title","notes","date","contactId","dealId","companyId","by","duration","properties"].includes(k))),
});

/* ── Activity icon ── */
function ActIcon({ type }) {
  const map = { call: { icon: "phone" }, email: { icon: "mail" }, meeting: { icon: "users" }, note: { icon: "edit" }, lifecycle: { icon: "refresh" }, task: { icon: "check" } };
  const i = map[type] || { icon: "dot" };
  return (
    <div style={{ width: 30, height: 30, borderRadius: 15, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(217,219,221,0.12)", flexShrink: 0 }}>
      <Icon name={i.icon} size={13} color="#FAFAFA" />
    </div>
  );
}

/* ── Collapsible section ── */

/* ── Recording play button (inline) ── */
function RecBtn({ url }) {
  const [playing, setPlaying] = useState(false);
  const audioRef = React.useRef(null);
  const recSid = (url.match(/Recordings\/(RE[a-f0-9]+)/i) || [])[1];
  const proxyUrl = recSid ? `/api/twilio/play?sid=${recSid}` : url;
  React.useEffect(() => {
    const a = new Audio(proxyUrl);
    audioRef.current = a;
    a.addEventListener("ended", () => setPlaying(false));
    return () => { a.pause(); a.src = ""; };
  }, [proxyUrl]);
  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play(); setPlaying(true); }
  };
  return (
    <button onClick={toggle} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.12)", background: playing ? "rgba(64,89,173,0.15)" : "rgba(255,255,255,0.06)", color: "#FAFAFA", fontSize: 10, cursor: "pointer", fontFamily: "inherit", marginTop: 4 }}>
      {playing ? "⏸ Pause" : "▶ Play Recording"}
    </button>
  );
}
function Section({ title, children, count, actions, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen !== false);
  return (
    <div style={{ borderBottom: "1px solid " + T.bdr }}>
      <div onClick={() => setOpen(!open)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", cursor: "pointer" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 10, color: T.txt }}><Icon name={open ? "chevronD" : "chevronR"} size={10} color={T.tx3} /></span>
          <span style={{ fontSize: 12, fontWeight: 400, color: T.txt }}>{title}</span>
          {count !== undefined && <span style={{ fontSize: 10, color: T.txt }}>({count})</span>}
        </div>
        {actions && <div onClick={e => e.stopPropagation()}>{actions}</div>}
      </div>
      {open && <div style={{ padding: "0 14px 12px" }}>{children}</div>}
    </div>
  );
}

/* LEAD_STAGES now come from store.leadStages */

export default function ContactRecord({ contact, onBack, onSelectDeal, onSelectCompany, isMobile }) {
  // Safety: ensure all expected fields exist with defaults
  const c = {
    tags: [], activities: [], properties: {}, ai: "", sent: "neutral",
    type: "SMB", industry: "", city: "", state: "", leadStage: "new",
    source: "Inbound", score: 0, status: "active", created: "",
    lastActivity: "", owner: "", fn: "", ln: "", email: "", ph: "",
    co: "", title: "",
    ...contact,
  };
  const store = useStore();
  const auth = useAuth();
  const toast = useToast();
  // Include both primary deals (via deals.contact_id mirror) AND deals where
  // this contact is a non-primary participant via the deal_contacts junction.
  const deals = useMemo(() => {
    const direct = store.deals.filter(d => d.ct === c.id);
    const junctionDealIds = new Set(
      (store.dealContacts || [])
        .filter(dc => dc.contact_id === c.id && !dc.is_primary)
        .map(dc => dc.deal_id)
    );
    const indirect = store.deals.filter(d => junctionDealIds.has(d.id));
    const seen = new Set();
    return [...direct, ...indirect].filter(d => seen.has(d.id) ? false : (seen.add(d.id), true));
  }, [store.deals, store.dealContacts, c.id]);
  const tasks = store.tasks.filter(t => t.ct === c.id);
  const scoreColor = "#FAFAFA";

  // Actions dropdown (About-section button)
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const actionsMenuRef = useRef(null);
  useEffect(() => {
    if (!showActionsMenu) return;
    const handler = (e) => { if (actionsMenuRef.current && !actionsMenuRef.current.contains(e.target)) setShowActionsMenu(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showActionsMenu]);

  // Add-to-list / Add-to-campaign / Delete modals
  const [showAddToListModal, setShowAddToListModal] = useState(false);
  const [showAddToCampaignModal, setShowAddToCampaignModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [availableLists, setAvailableLists] = useState(null); // null = loading, [] = loaded empty
  const [availableCampaigns, setAvailableCampaigns] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const orgIdForActions = auth.currentUser?._orgId || window.__coreflow_org_id;
  const userIdForActions = store.currentUserId || auth.currentUser?._dbId;

  // Load lists when the modal opens
  useEffect(() => {
    if (!showAddToListModal || !orgIdForActions) return;
    let cancelled = false;
    setAvailableLists(null);
    supabase.from("contact_lists").select("id, name, type").eq("org_id", orgIdForActions).eq("type", "static").order("name").then(({ data, error }) => {
      if (cancelled) return;
      if (error) { toast?.error?.("Failed to load lists: " + error.message); setAvailableLists([]); return; }
      setAvailableLists(data || []);
    });
    return () => { cancelled = true; };
  }, [showAddToListModal, orgIdForActions]);

  // Load campaigns when the modal opens
  useEffect(() => {
    if (!showAddToCampaignModal || !orgIdForActions) return;
    let cancelled = false;
    setAvailableCampaigns(null);
    supabase.from("campaigns").select("id, name, status").eq("org_id", orgIdForActions).in("status", ["active", "draft"]).order("created_at", { ascending: false }).then(({ data, error }) => {
      if (cancelled) return;
      if (error) { toast?.error?.("Failed to load campaigns: " + error.message); setAvailableCampaigns([]); return; }
      setAvailableCampaigns(data || []);
    });
    return () => { cancelled = true; };
  }, [showAddToCampaignModal, orgIdForActions]);

  const addToList = async (listId, listName) => {
    if (!c.id) return;
    setActionBusy(true);
    try {
      const { error } = await supabase.from("contact_list_members").upsert(
        { list_id: listId, contact_id: c.id, added_by: userIdForActions || null },
        { onConflict: "list_id,contact_id" }
      );
      if (error) throw error;
      toast?.success?.(`Added to ${listName}`);
      setShowAddToListModal(false);
    } catch (err) {
      toast?.error?.("Failed to add to list: " + (err.message || "unknown error"));
    } finally {
      setActionBusy(false);
    }
  };

  const addToCampaign = async (campaignId, campaignName) => {
    if (!c.id || !orgIdForActions) return;
    setActionBusy(true);
    try {
      const { error } = await supabase.from("campaign_entries").insert({
        org_id: orgIdForActions, campaign_id: campaignId, contact_id: c.id,
        status: "pending", attempts: 0, current_step: 0,
      });
      if (error) {
        // 23505 = unique violation (Postgres) — contact already in campaign
        if (error.code === "23505" || /duplicate|unique/i.test(error.message || "")) {
          toast?.info?.(`Already in ${campaignName}`);
          setShowAddToCampaignModal(false);
          return;
        }
        throw error;
      }
      toast?.success?.(`Added to ${campaignName}`);
      setShowAddToCampaignModal(false);
    } catch (err) {
      toast?.error?.("Failed to add to campaign: " + (err.message || "unknown error"));
    } finally {
      setActionBusy(false);
    }
  };

  const confirmDeleteContact = async () => {
    if (!c.id) return;
    setActionBusy(true);
    try {
      await store.deleteContact(c.id);
      setShowDeleteModal(false);
      // store.deleteContact already toasts on success and rolls back on failure.
      if (onBack) onBack();
    } catch (err) {
      toast?.error?.("Failed to delete contact: " + (err.message || "unknown error"));
    } finally {
      setActionBusy(false);
    }
  };

  // Inline add-tag input
  const [addingTag, setAddingTag] = useState(false);
  const [newTagValue, setNewTagValue] = useState("");
  const [tagSaving, setTagSaving] = useState(false);
  const saveNewTag = async () => {
    const t = (newTagValue || "").trim();
    if (!t) { setAddingTag(false); return; }
    if ((c.tags || []).includes(t)) { toast?.info?.("Tag already exists"); setNewTagValue(""); return; }
    setTagSaving(true);
    try {
      const nextTags = [...(c.tags || []), t];
      await store.updateContact(c.id, { tags: nextTags });
      setNewTagValue("");
      setAddingTag(false);
      toast?.success?.("Tag added");
    } catch (err) {
      toast?.error?.("Failed to add tag: " + (err.message || "unknown error"));
    } finally {
      setTagSaving(false);
    }
  };

  // Attachments upload
  const attachmentInputRef = useRef(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const handleAttachmentSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !c.id) return;
    if (file.size > 25 * 1024 * 1024) { toast?.error?.("File too large (max 25MB)"); return; }
    setUploadingAttachment(true);
    try {
      const orgId = window.__coreflow_org_id || "org";
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const path = `contacts/${orgId}/${c.id}/${Date.now()}-${safeName}`;
      const { error: upErr } = await supabase.storage.from("chat-files").upload(path, file);
      if (upErr) throw upErr;
      const { data: urlData } = await supabase.storage.from("chat-files").createSignedUrl(path, 60 * 60 * 24 * 30); // 30 days
      const record = { name: file.name, path, url: urlData?.signedUrl || null, size: file.size, type: file.type, uploadedAt: new Date().toISOString() };
      // Persist under properties.attachments — `attachments` isn't in dbUpdateContact's
      // known-keys whitelist, so anything passed at the top level gets nested into
      // `properties` anyway. Writing directly to properties keeps reads consistent.
      const existingProps = c.properties || {};
      const nextAttachments = [...(existingProps.attachments || []), record];
      await store.updateContact(c.id, { properties: { ...existingProps, attachments: nextAttachments } });
      toast?.success?.("File uploaded");
    } catch (err) {
      toast?.error?.("Upload failed: " + (err.message || "unknown error"));
    } finally {
      setUploadingAttachment(false);
    }
  };

  // ── Call recordings from Supabase ──
  const [callRecordings, setCallRecordings] = useState([]);
  useEffect(() => {
    if (!c.id) return;
    supabase.from("call_logs").select("*")
      .eq("contact_id", c.id)
      .order("started_at", { ascending: false })
      .limit(50)
      .then(({ data }) => {
        if (data) setCallRecordings(data);
      });
  }, [c.id]);

  // ── Per-contact activities (full window, bypasses 200-row global cache) ──
  // store.js's auth-boot loader pulls only the 200 most-recent activities
  // org-wide. After today's bulk import + server-side email automation
  // (outbound-sender, check-replies, inbound, etc.) that 200-row window
  // got saturated by email-type rows, so older non-email activity (notes,
  // calls, meetings, tasks, lifecycle) for any specific contact got
  // evicted from the cache. This direct query restores the full per-contact
  // history without affecting the global cache used by other dashboards.
  const [contactActivities, setContactActivities] = useState([]);
  useEffect(() => {
    if (!c?.id) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("activities")
        .select("*")
        .eq("contact_id", c.id)
        .order("created_at", { ascending: false })
        .limit(500);
      if (cancelled) return;
      if (error) {
        console.error("[ContactRecord] activities fetch failed:", error);
        return;
      }
      setContactActivities((data || []).map(dbActivityToLocal));
    })();
    return () => { cancelled = true; };
  }, [c?.id]);

  // Use saved leadStage from DB, fall back to index 0
  const getLeadStageIdx = () => {
    if (c.leadStage) {
      const idx = store.leadStages.findIndex(s => s.name.toLowerCase() === c.leadStage.toLowerCase());
      if (idx >= 0) return idx;
    }
    return 0;
  };
  const [leadStage, setLeadStage] = useState(getLeadStageIdx());

  // Activity/note state
  const [actTab, setActTab] = useState("Activity");
  const [actSearch, setActSearch] = useState("");
  const [noteText, setNoteText] = useState("");
  const [showQuickAction, setShowQuickAction] = useState(null);
  const [editingActivity, setEditingActivity] = useState(null);
  // Call dialer modal state — stays in ContactRecord because the Twilio
  // dialer is contact-scoped. QuickActionPanel delegates Call to this
  // modal via the onLaunchDialer prop.
  const [callOutcome, setCallOutcome] = useState("Connected");
  const [callDuration, setCallDuration] = useState("5 min");
  const [showCallModal, setShowCallModal] = useState(false);
  const [callActive, setCallActive] = useState(false);
  const [callTimer, setCallTimer] = useState(0);
  const [callEnded, setCallEnded] = useState(false);

  // Listen for Twilio call events to sync modal state.
  // callActive is read inside handleEnd, so mirror it via callActiveRef so
  // the [] deps boundary doesn't strand the read. Previous version had
  // deps [callActive] + cleanup that only cleared the interval — every
  // callActive flip re-registered new handleEnd listeners on the global
  // twilio singleton without removing the prior generation. Now registers
  // once on mount and offCallEvent's both event channels on unmount.
  const callActiveRef = useRef(callActive);
  useEffect(() => { callActiveRef.current = callActive; }, [callActive]);
  useEffect(() => {
    const handleEnd = () => {
      if (callActiveRef.current) {
        clearInterval(window._callInterval);
        setCallActive(false);
        setCallEnded(true);
      }
    };
    onCallEvent("ended", handleEnd);
    onCallEvent("rejected", handleEnd);
    return () => {
      clearInterval(window._callInterval);
      offCallEvent("ended", handleEnd);
      offCallEvent("rejected", handleEnd);
    };
  }, []);
  // Add deal modal
  const [showAddDeal, setShowAddDeal] = useState(false);
  const [newDealName, setNewDealName] = useState("");
  const [newDealVal, setNewDealVal] = useState("");
  const [newDealStage, setNewDealStage] = useState("Discovery");
  const [newDealClose, setNewDealClose] = useState("");

  const actTabs = ["Activity", "Notes", "Emails", "Calls", "Tasks", "Meetings"];
  const typeMap = { Notes: "note", Emails: "email", Calls: "call", Tasks: "task", Meetings: "meeting" };

  const filteredActs = useMemo(() => {
    // Direct per-contact query (full window, .limit(500) — sees lifecycle/
    // notes/meetings/tasks/calls the 200-row global cache evicts).
    const directActs = contactActivities.map(a => ({ ...a, source: "direct" }));
    // Global cache: still merged so optimistic inserts (addNote/logCall/
    // logEmail/logMeeting/updateDealStage paths in store.js prepend to
    // store.activities) appear immediately without waiting for the next
    // per-contact refetch.
    const globalActs = store.activities.filter(a => a.contactId === c.id).map(a => ({
      ...a, source: "global"
    }));
    // Seed: c.activities (denormalized cache built from same 200-row snapshot
    // at store.js boot). Kept as a third-tier safety net; id-dedup handles
    // overlap with direct/global.
    const seedActs = (c.activities || []).map(a => ({ ...a, source: "seed" }));
    // Merge call recordings as activities
    const callActs = callRecordings.map(cl => ({
      type: "call",
      title: (cl.direction === "inbound" ? "Inbound" : "Outbound") + " call" + (cl.duration ? ` (${Math.floor(cl.duration/60)}:${String(cl.duration%60).padStart(2,"0")})` : ""),
      notes: cl.status === "completed" ? "Connected" : cl.status || "",
      date: cl.started_at,
      recording_url: cl.recording_url || null,
      duration: cl.duration,
      by: cl.user_id,
      source: "call_log",
    }));

    // Dedup by id across direct + global + seed (call_log entries don't have
    // activity ids and are deduped separately below by minute-truncated date).
    // Order matters: directActs first → direct query wins on duplicates,
    // because it has the full per-contact window; global/seed are subsets
    // of the 200-row org-wide cache.
    const seenIds = new Set();
    const merged = [];
    [...directActs, ...globalActs, ...seedActs].forEach(a => {
      if (a.id && seenIds.has(a.id)) return;
      if (a.id) seenIds.add(a.id);
      merged.push(a);
    });

    // Dedup call_log entries against any merged 'call'-type activity by
    // minute-truncated start time (legacy logCallToDb path writes both an
    // activities row and a call_logs row).
    const existingCallKeys = new Set(
      merged.filter(a => a.type === "call").map(a => (a.date || "").slice(0, 16))
    );
    const uniqueCalls = callActs.filter(a => !existingCallKeys.has((a.date || "").slice(0, 16)));

    let acts = [...uniqueCalls, ...merged].sort((a, b) => new Date(b.date) - new Date(a.date));
    if (actTab !== "Activity") acts = acts.filter(a => a.type === typeMap[actTab]);
    if (actSearch) acts = acts.filter(a => (a.title + " " + (a.notes || "")).toLowerCase().includes(actSearch.toLowerCase()));
    return acts;
  }, [c, actTab, actSearch, store.activities, callRecordings, contactActivities]);

  const stgC = { Discovery: T.tx3, Qualification: "#D4763A", Proposal: "#6B5B95", Negotiation: T.blue, "Closed Won": "#2E8B57" };

  const handleCreateDeal = () => {
    if (!newDealName.trim()) return;
    const val = parseInt(newDealVal) || 0;
    store.createDeal({
      name: newDealName, co: c.co, ct: c.id,
      val, stage: newDealStage, prob: newDealStage === "Closed Won" ? 100 : 20,
      close: newDealClose || "", owner: c.owner || "", ownerId: c.ownerId || "",
    });
    store.addActivity({ type: "lifecycle", contactId: c.id, title: "Deal created: " + newDealName, notes: "$" + val.toLocaleString() + " - " + newDealStage });
    setShowAddDeal(false);
    setNewDealName(""); setNewDealVal(""); setNewDealStage("Discovery"); setNewDealClose("");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: "transparent" }}>

      {/* ─── CHEVRON LEAD STAGE BAR ─── */}
      {!isMobile && <div style={{ display: "flex", flexShrink: 0, height: 36, background: "transparent", margin: "8px 24px 0" }}>
        {store.leadStages.map((stg, i) => {
          const isCurrent = i === leadStage;
          const isPast = i < leadStage;
          const isLast = i === store.leadStages.length - 1;
          const bg = isCurrent ? "#4059AD" : "#2C2E33";
          const txtColor = "#FAFAFA";
          const strokeColor = "rgba(217,219,221,0.25)";
          return (
            <div key={stg.id} onClick={() => { setLeadStage(i); store.updateContact(c.id, { leadStage: stg.name }); }}
              style={{ flex: 1, position: "relative", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg viewBox="0 0 200 40" preserveAspectRatio="none" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
                <path d={i === 0
                  ? "M0,0 L185,0 L200,20 L185,40 L0,40 Z"
                  : isLast
                  ? "M0,0 L200,0 L200,40 L0,40 L15,20 Z"
                  : "M0,0 L185,0 L200,20 L185,40 L0,40 L15,20 Z"
                } fill={bg} stroke={strokeColor} strokeWidth="1" />
              </svg>
              <div style={{ position: "relative", zIndex: 1, display: "flex", alignItems: "center", gap: 6 }}>
                
                <span style={{ fontSize: 11, fontWeight: 400, color: txtColor }}>{stg.name}</span>
              </div>
            </div>
          );
        })}
      </div>}

      {/* ─── THREE COLUMN LAYOUT ─── */}
      <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", flex: 1, overflow: isMobile ? "auto" : "hidden" }}>

      {/* LEFT SIDEBAR */}
      <div style={{ width: isMobile ? "100%" : 290, flexShrink: 0, borderRight: isMobile ? "none" : "1px solid " + T.bdr, borderBottom: isMobile ? "1px solid " + T.bdr : "none", overflowY: isMobile ? "visible" : "auto", background: "#2C2E33" }}>
        <div style={{ padding: isMobile ? 12 : 16 }}>
          <button onClick={onBack} style={{ background: "none", border: "none", color: "#FAFAFA", fontSize: 11, fontWeight: 400, marginBottom: 12, display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
            <span style={{ fontSize: 14 }}><Icon name="chevronL" size={14} color="#FAFAFA" /></span> Contacts
          </button>

          <div style={{ marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 400, color: T.txt }}>{c.fn} {c.ln}</div>
              <div style={{ fontSize: 11, color: T.txt }}>{c.title}</div>
              <div style={{ fontSize: 11, color: T.tx2 }}>{c.co}</div>
              {/* Disposition status chips (commit D). Read-only in v1 —
                  reps clear flags by recording a different disposition.
                  All three may be present simultaneously (a contact could
                  be both qualified and have a stale requeue_after) so
                  each renders independently. */}
              {(c.do_not_call || c.qualified_at || (c.requeue_after && new Date(c.requeue_after).getTime() > Date.now())) && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                  {c.do_not_call && (
                    <span style={{
                      padding: "2px 8px", borderRadius: 4, fontSize: 10, fontWeight: 500,
                      background: "rgba(229,57,53,0.12)", color: "#E53935",
                      border: "1px solid rgba(229,57,53,0.3)",
                    }}>DNC</span>
                  )}
                  {c.qualified_at && (
                    <span style={{
                      padding: "2px 8px", borderRadius: 4, fontSize: 10, fontWeight: 500,
                      background: "rgba(46,139,87,0.12)", color: "#2E8B57",
                      border: "1px solid rgba(46,139,87,0.3)",
                    }}>Qualified</span>
                  )}
                  {c.requeue_after && new Date(c.requeue_after).getTime() > Date.now() && (
                    <span style={{
                      padding: "2px 8px", borderRadius: 4, fontSize: 10, fontWeight: 500,
                      background: "rgba(212,118,58,0.12)", color: "#D4763A",
                      border: "1px solid rgba(212,118,58,0.3)",
                    }}>Requeue {new Date(c.requeue_after).toLocaleDateString()}</span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Quick actions - FUNCTIONAL */}
          <div style={{ display: "flex", gap: 4, marginBottom: 12 }}>
            {[
              { l: "Note", ic: "edit", action: () => { setShowQuickAction(showQuickAction === "note" ? null : "note"); setActTab("Activity"); } },
              { l: "Email", ic: "mail", action: () => { setShowQuickAction(showQuickAction === "email" ? null : "email"); setActTab("Activity"); } },
              { l: "Call", ic: "phone", action: () => { setShowCallModal(true); setCallActive(false); setCallEnded(false); setCallTimer(0); setNoteText(""); setCallOutcome("Connected"); } },
              { l: "Task", ic: "check", action: () => { setShowQuickAction(showQuickAction === "task" ? null : "task"); setActTab("Activity"); } },
              { l: "Meeting", ic: "users", action: () => { setShowQuickAction(showQuickAction === "meeting" ? null : "meeting"); setActTab("Activity"); } },
            ].map(a => (
              <div key={a.l} onClick={a.action} style={{
                flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 2, padding: "7px 2px", borderRadius: 4,
                border: "1px solid " + (showQuickAction === a.l.toLowerCase() ? T.blue + "40" : T.bdr),
                background: showQuickAction === a.l.toLowerCase() ? T.blue + "08" : T.card,
                cursor: "pointer", transition: "all 0.15s",
              }}>
                <Icon name={a.ic} size={15} color={showQuickAction === a.l.toLowerCase() ? T.blue : T.tx2} />
                <span style={{ fontSize: 8, color: T.txt }}>{a.l}</span>
              </div>
            ))}
          </div>

          {/* Status */}
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 11, color: T.txt }}>
            <span><span style={{ color: "rgba(255,255,255,0.4)" }}>Status</span> <span style={{ fontWeight: 400, textTransform: "capitalize" }}>{c.status}</span></span>
            <span><span style={{ color: "rgba(255,255,255,0.4)" }}>Sentiment</span> <span style={{ fontWeight: 400, textTransform: "capitalize" }}>{c.sent || "neutral"}</span></span>
            <span><span style={{ color: "rgba(255,255,255,0.4)" }}>Score</span> <span style={{ fontWeight: 400 }}>{c.score}</span></span>
          </div>
        </div>

        {/* AI Analysis */}
        <div style={{ margin: "0 12px 12px", padding: "10px 12px", borderRadius: 5, background: T.blue + "06", border: "1px solid " + T.blue + "15" }}>
          <div style={{ fontSize: 9, color: T.tx3, fontWeight: 400, letterSpacing: ".08em", marginBottom: 4, display: "flex", alignItems: "center", gap: 4 }}><Icon name="ai" size={11} color={"#FAFAFA"} /> CORE ANALYSIS</div>
          <div style={{ fontSize: 11, color: T.txt, lineHeight: 1.55 }}>{c.ai}</div>
        </div>

        {/* About this contact */}
        <Section title="About this contact" defaultOpen={true} actions={
          <div ref={actionsMenuRef} style={{ position: "relative" }}>
            <button onClick={e => { e.stopPropagation(); setShowActionsMenu(v => !v); }} style={{ padding: "2px 6px", borderRadius: 3, border: "1px solid " + T.bdr, background: "transparent", color: T.txt, fontSize: 9, cursor: "pointer" }}>Actions ▾</button>
            {showActionsMenu && (
              <div style={{ position: "absolute", top: "100%", right: 0, marginTop: 4, minWidth: 160, background: "#2C2E33", border: "1px solid " + T.bdr, borderRadius: 6, boxShadow: "0 8px 24px rgba(0,0,0,0.4)", zIndex: 20, padding: 4 }}>
                {[
                  { label: "Add to List", action: () => setShowAddToListModal(true) },
                  { label: "Add to Campaign", action: () => setShowAddToCampaignModal(true) },
                  { label: "Delete contact", action: () => setShowDeleteModal(true), danger: true },
                ].map(item => (
                  <button key={item.label} onClick={() => { setShowActionsMenu(false); item.action(); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "6px 10px", fontSize: 11, background: "transparent", border: "none", color: item.danger ? "#E53935" : T.txt, cursor: "pointer", borderRadius: 4, fontFamily: "inherit" }}>{item.label}</button>
                ))}
              </div>
            )}
          </div>
        }>
          <ContactForm
            values={c}
            onChange={updates => store.updateContact(c.id, updates)}
            teamMembers={store.teamMembers}
            getOwnerName={store.getOwnerName}
            customFields={store.customFields?.contacts}
            store={store}
          />
        </Section>

        <Section title="Tags">
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
            {(c.tags || []).map(tag => <Badge key={tag} color={"#3D8B8B"}>{tag}</Badge>)}
            {addingTag ? (
              <input
                autoFocus
                value={newTagValue}
                onChange={e => setNewTagValue(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter") { e.preventDefault(); saveNewTag(); }
                  else if (e.key === "Escape") { setAddingTag(false); setNewTagValue(""); }
                }}
                onBlur={() => { if (!tagSaving && !newTagValue.trim()) setAddingTag(false); }}
                disabled={tagSaving}
                placeholder="tag name"
                style={{ padding: "2px 6px", borderRadius: 3, border: "1px solid " + T.bdr, background: "rgba(255,255,255,0.04)", color: T.txt, fontSize: 10, outline: "none", fontFamily: "inherit", width: 90 }}
              />
            ) : (
              <button onClick={() => setAddingTag(true)} style={{ padding: "2px 8px", borderRadius: 3, border: "1px dashed " + T.bdr, background: "transparent", color: T.txt, fontSize: 10, cursor: "pointer" }}>+ Add</button>
            )}
          </div>
        </Section>
      </div>

      {/* CENTER: Activity Timeline */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Tabs */}
        <div style={{ borderBottom: "1px solid rgba(217,219,221,0.08)" }}>
          <div style={{ display: "flex", padding: "0 16px" }}>
            {actTabs.map(t => (
              <button key={t} onClick={() => setActTab(t)} style={{
                padding: "10px 14px", border: "none", background: "transparent", cursor: "pointer", fontFamily: "inherit",
                color: T.txt, opacity: actTab === t ? 1 : 0.4, fontSize: 11, fontWeight: 400,
                borderBottom: actTab === t ? "2px solid " + T.blue : "2px solid transparent",
              }}>{t}</button>
            ))}
          </div>
        </div>

        {/* Search + Add button row */}
        <div style={{ padding: "10px 16px", borderBottom: "1px solid rgba(217,219,221,0.08)", display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, padding: "6px 12px", flex: 1 }}>
            <Icon name="search" size={13} color={T.txt} />
            <input value={actSearch} onChange={e => setActSearch(e.target.value)} placeholder="Search activities..."
              style={{ border: "none", outline: "none", background: "transparent", color: T.txt, fontSize: 12, width: "100%", fontFamily: "inherit" }} />
            <span style={{ fontSize: 10, color: T.txt, opacity: 0.4, flexShrink: 0 }}>({filteredActs.length})</span>
          </div>
          <button onClick={() => {
            const tabAction = { Notes: "note", Emails: "email", Calls: "call", Tasks: "task", Meetings: "meeting", Activity: "note" };
            const key = tabAction[actTab];
            setShowQuickAction(showQuickAction === key ? null : key);
          }} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 18px", borderRadius: 8, border: "none", background: T.blue, color: "#D9DBDD", fontSize: 11, fontWeight: 400, letterSpacing: ".04em", textTransform: "uppercase", cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", flexShrink: 0 }}>
            + {{ Notes: "ADD NOTE", Emails: "LOG EMAIL", Calls: "LOG CALL", Tasks: "ADD TASK", Meetings: "LOG MEETING", Activity: "ADD NOTE" }[actTab]}
          </button>
        </div>

          {/* Quick action input panel */}
          {showQuickAction && (
            <QuickActionPanel
              type={showQuickAction}
              contactId={c.id}
              contactPhone={c.ph}
              contactName={(c.fn || "") + " " + (c.ln || "")}
              onLaunchDialer={() => {
                setShowCallModal(true);
                setCallActive(false);
                setCallEnded(false);
                setCallTimer(0);
                setNoteText("");
                setCallOutcome("Connected");
              }}
              onClose={() => setShowQuickAction(null)}
              onCreated={() => setShowQuickAction(null)}
            />
          )}

        {/* Timeline */}
        <div style={{ flex: 1, overflow: "auto", padding: "16px 16px" }}>
          {filteredActs.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: T.txt }}>
              <div style={{ fontSize: 13 }}>No {actTab === "Activity" ? "activities" : actTab.toLowerCase()} yet</div>
            </div>
          ) : (
            filteredActs.map((act, i) => {
              // Edit/delete affordance:
              //   - note/meeting/call/email: own-or-admin, only when manually
              //     authored (real `by` UUID, not "System").
              //   - lifecycle: anyone with record access can delete (system-
              //     generated, no meaningful author). Modal renders as
              //     read-only with delete-only actions.
              const editableTypes = ["note", "meeting", "call", "email", "lifecycle"];
              const isAdmin = auth.currentUser?.role === "admin";
              const isEditable = editableTypes.includes(act.type) && (
                act.type === "lifecycle"
                  ? true
                  : (act.by && act.by !== "System" && (act.by === store.currentUserId || isAdmin))
              );
              return (
                <div key={i} style={{ display: "flex", gap: 12, marginBottom: 0 }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 30 }}>
                    <ActIcon type={act.type} />
                    {i < filteredActs.length - 1 && <div style={{ width: 1, flex: 1, background: T.bdr, minHeight: 16 }} />}
                  </div>
                  <div style={{ flex: 1, paddingBottom: 20 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 3 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 10, fontWeight: 400, color: "#FAFAFA", background: "rgba(255,255,255,0.08)", border: "1px solid rgba(217,219,221,0.12)", padding: "2px 8px", borderRadius: 4, textTransform: "uppercase", letterSpacing: ".04em" }}>{act.type}</span>
                        <span style={{ fontSize: 12, fontWeight: 400, color: T.txt }}>{act.title}</span>
                        {act.duration && <span style={{ fontSize: 10, color: T.txt }}>{act.duration}</span>}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0, marginLeft: 8 }}>
                        <span style={{ fontSize: 10, color: T.txt }}>{cfDateTime(act.date)}</span>
                        {isEditable && (
                          <button onClick={() => setEditingActivity(act)} title="Edit"
                            style={{ background: "transparent", border: "none", padding: 4, cursor: "pointer", opacity: 0.5, display: "flex", alignItems: "center" }}
                            onMouseEnter={e => e.currentTarget.style.opacity = "1"}
                            onMouseLeave={e => e.currentTarget.style.opacity = "0.5"}>
                            <Icon name="edit" size={12} color={T.txt} />
                          </button>
                        )}
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: T.txt, lineHeight: 1.6, padding: "4px 0", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{act.notes}</div>
                    {act.recording_url && <RecBtn url={act.recording_url} />}
                    {act.by && act.by !== "System" && <div style={{ fontSize: 10, color: T.txt }}>by {store.getOwnerName(act.by) !== "—" ? store.getOwnerName(act.by) : act.by}</div>}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* RIGHT SIDEBAR */}
      <div style={{ width: isMobile ? "100%" : 300, flexShrink: 0, borderLeft: isMobile ? "none" : "1px solid " + T.bdr, borderTop: isMobile ? "1px solid " + T.bdr : "none", overflowY: isMobile ? "visible" : "auto", background: "#2C2E33" }}>

        {/* Lead Stage - CLICKABLE, reads from store */}

        {/* Company card — links to CompanyRecord when contact is linked to a company */}
        {c.company_id && (() => {
          const linkedCompany = (store.companies || []).find(co => co.id === c.company_id);
          const displayName = linkedCompany?.name || c.co;
          if (!displayName) return null;
          const canClick = !!linkedCompany && !!onSelectCompany;
          return (
            <Section title="Company" defaultOpen={true}>
              <div
                onClick={canClick ? () => onSelectCompany(linkedCompany.id) : undefined}
                style={{ padding: "10px 12px", borderRadius: 6, background: T.sf, border: "1px solid " + T.bdr, cursor: canClick ? "pointer" : "default", transition: "border-color 0.15s" }}
                onMouseEnter={e => { if (canClick) e.currentTarget.style.borderColor = T.blue + "50"; }}
                onMouseLeave={e => { if (canClick) e.currentTarget.style.borderColor = T.bdr; }}
              >
                <div style={{ fontSize: 12, fontWeight: 400, color: T.txt }}>{displayName}</div>
                {linkedCompany?.industry && <div style={{ fontSize: 10, color: T.tx3, marginTop: 2 }}>{linkedCompany.industry}</div>}
              </div>
            </Section>
          );
        })()}

        {/* Deals - CLICKABLE */}
        <Section title="Deals" count={deals.length} actions={
          <button onClick={() => setShowAddDeal(!showAddDeal)} style={{ padding: "2px 8px", borderRadius: 3, border: "1px solid " + (showAddDeal ? "rgba(217,219,221,0.25)" : T.bdr), background: showAddDeal ? "rgba(255,255,255,0.08)" : "transparent", color: "#FAFAFA", fontSize: 10, cursor: "pointer" }}>+ Add</button>
        }>
          {/* Add deal form */}
          {showAddDeal && (
            <div style={{ padding: 10, marginBottom: 8, borderRadius: 5, background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)" }}>
              <div style={{ fontSize: 10, fontWeight: 400, color: T.tx3, marginBottom: 8, letterSpacing: ".06em" }}>NEW DEAL</div>
              <input value={newDealName} onChange={e => setNewDealName(e.target.value)} placeholder="Deal name..." autoFocus
                style={{ width: "100%", background: "#2C2E33", border: "1px solid rgba(255,255,255,0.60)", borderRadius: 4, padding: "6px 8px", color: T.txt, fontSize: 11, outline: "none", marginBottom: 6, boxSizing: "border-box" }} />
              <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                <input value={newDealVal} onChange={e => setNewDealVal(e.target.value)} placeholder="Value ($)" type="number"
                  style={{ flex: 1, background: "#2C2E33", border: "1px solid rgba(255,255,255,0.60)", borderRadius: 4, padding: "6px 8px", color: T.txt, fontSize: 11, outline: "none" }} />
                <input value={newDealClose} onChange={e => setNewDealClose(e.target.value)} type="date"
                  style={{ flex: 1, background: "#2C2E33", border: "1px solid rgba(255,255,255,0.60)", borderRadius: 4, padding: "6px 8px", color: T.txt, fontSize: 11, outline: "none" }} />
              </div>
              <div style={{ display: "flex", gap: 3, marginBottom: 8 }}>
                {(store.stages || []).map(s => (
                  <button key={s} onClick={() => setNewDealStage(s)} style={{
                    flex: 1, padding: "4px 2px", borderRadius: 3, fontSize: 7, fontWeight: 400, cursor: "pointer",
                    border: newDealStage === s ? "2px solid " + (stgC[s] || T.blue) : "1px solid " + T.bdr,
                    background: newDealStage === s ? (stgC[s] || T.blue) + "12" : T.sf,
                    color: newDealStage === s ? (stgC[s] || T.blue) : T.tx3,
                  }}>{s}</button>
                ))}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <button onClick={() => { setShowAddDeal(false); setNewDealName(""); setNewDealVal(""); }} style={{ background: "none", border: "none", color: T.txt, fontSize: 10, cursor: "pointer" }}>Cancel</button>
                <Btn primary style={{ fontSize: 10 }} onClick={handleCreateDeal}>Create Deal</Btn>
              </div>
            </div>
          )}
          {deals.length === 0 && !showAddDeal ? (
            <div style={{ fontSize: 11, color: T.txt, padding: "8px 0" }}>No associated deals</div>
          ) : deals.map(d => (
            <div key={d.id} onClick={() => onSelectDeal && onSelectDeal(d.id)} style={{ padding: "8px 10px", marginBottom: 4, borderRadius: 4, background: "#2C2E33", border: "1.5px solid rgba(217,219,221,0.12)", cursor: "pointer", transition: "border-color 0.15s" }}
              onMouseEnter={e => e.currentTarget.style.borderColor = T.blue + "50"}
              onMouseLeave={e => e.currentTarget.style.borderColor = T.bdr}>
              <div style={{ fontSize: 11, fontWeight: 400, color: "#FAFAFA" }}>{d.name}</div>
              <div style={{ fontSize: 10, color: T.txt, marginTop: 2 }}>Amount: <span style={{ color: "#FAFAFA", fontWeight: 400 }}>${d.val.toLocaleString()}</span></div>
              <div style={{ fontSize: 10, color: T.txt, marginTop: 1 }}>Close: {d.close}</div>
              <div style={{ fontSize: 10, color: T.txt, marginTop: 1 }}>Stage: <span style={{ color: "#FAFAFA", fontWeight: 400 }}>{d.stage}</span></div>
            </div>
          ))}
        </Section>

        {/* Tasks - TOGGLEABLE */}
        <Section title="Tasks" count={tasks.length} defaultOpen={false} actions={
          <button onClick={() => { setShowQuickAction(showQuickAction === "task" ? null : "task"); setActTab("Activity"); }} style={{ padding: "2px 8px", borderRadius: 3, border: "1px solid " + T.bdr, background: "transparent", color: "#FAFAFA", fontSize: 10, cursor: "pointer" }}>+ Add</button>
        }>
          {tasks.length === 0 ? (
            <div style={{ fontSize: 11, color: T.txt, padding: "8px 0" }}>No tasks</div>
          ) : tasks.map(t => (
            <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 0", borderBottom: "1px solid " + T.bdr, opacity: t.done ? 0.4 : 1 }}>
              <div onClick={() => store.toggleTask(t.id)} style={{ width: 14, height: 14, borderRadius: 3, border: "2px solid " + (t.done ? "#2E8B57" : T.bdr), background: t.done ? "#2E8B57" : "transparent", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, color: "#fff", cursor: "pointer" }}>
                {t.done ? <Icon name="check" size={8} color={"#2E8B57"} /> : ""}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: T.txt, textDecoration: t.done ? "line-through" : "none" }}>{t.title}</div>
                <div style={{ fontSize: 9, color: T.txt }}>Due: {t.due}</div>
              </div>
              <Badge color={t.pri === "high" ? "#E53935" : t.pri === "medium" ? "#D4763A" : T.tx3}>{t.pri}</Badge>
            </div>
          ))}
        </Section>

        {/* Attachments */}
        <Section title="Attachments" count={(c.properties?.attachments || []).length} defaultOpen={false}>
          {(c.properties?.attachments || []).length === 0 ? (
            <div style={{ fontSize: 11, color: T.txt, padding: "8px 0" }}>No attachments yet</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: "6px 0" }}>
              {(c.properties?.attachments || []).map((a, i) => (
                <a key={i} href={a.url || "#"} target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", borderRadius: 4, background: "rgba(255,255,255,0.04)", border: "1px solid " + T.bdr, textDecoration: "none", color: T.txt, fontSize: 11 }}>
                  <Icon name="file" size={12} color={T.tx3} />
                  <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</span>
                  <span style={{ fontSize: 9, color: T.tx3 }}>{a.size ? (a.size / 1024).toFixed(0) + "KB" : ""}</span>
                </a>
              ))}
            </div>
          )}
          <input ref={attachmentInputRef} type="file" onChange={handleAttachmentSelected} style={{ display: "none" }} />
          <Btn style={{ fontSize: 10 }} onClick={() => attachmentInputRef.current?.click()} disabled={uploadingAttachment}>
            {uploadingAttachment ? "Uploading..." : "Upload file"}
          </Btn>
        </Section>

        {/* Notes */}
        <Section title="Recent Notes" count={filteredActs.filter(a => a.type === "note").length} defaultOpen={false}>
          {filteredActs.filter(a => a.type === "note").length === 0 ? (
            <div style={{ fontSize: 11, color: T.txt, padding: "8px 0" }}>No notes yet. Use the Note button to add one.</div>
          ) : filteredActs.filter(a => a.type === "note").slice(0, 5).map((n, i) => (
            <div key={i} style={{ padding: "6px 0", borderBottom: "1px solid " + T.bdr }}>
              <div style={{ fontSize: 11, color: T.txt }}>{n.title}</div>
              <div style={{ fontSize: 10, color: T.txt, marginTop: 2 }}>{n.notes}</div>
              <div style={{ fontSize: 9, color: T.txt, marginTop: 2 }}>{cfDateTime(n.date)}</div>
            </div>
          ))}
        </Section>

        {/* Change History */}
        <ChangeHistorySection entityType="contact" entityId={c.id} />
      </div>

      {/* ═══ ADD TO LIST MODAL ═══ */}
      {showAddToListModal && (
        <div onClick={() => !actionBusy && setShowAddToListModal(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()} style={{ width: 380, maxWidth: "92vw", maxHeight: "70vh", background: "#2C2E33", border: "1px solid " + T.bdr, borderRadius: 10, padding: 20, display: "flex", flexDirection: "column" }}>
            <div style={{ fontSize: 14, fontWeight: 400, color: T.txt, marginBottom: 4 }}>Add to List</div>
            <div style={{ fontSize: 11, color: T.tx3, marginBottom: 14 }}>Pick a static list to add {c.fn || "this contact"} to.</div>
            <div style={{ flex: 1, overflowY: "auto", marginBottom: 12 }}>
              {availableLists === null ? (
                <div style={{ fontSize: 11, color: T.tx3, padding: "12px 0", textAlign: "center" }}>Loading lists...</div>
              ) : availableLists.length === 0 ? (
                <div style={{ fontSize: 11, color: T.tx3, padding: "12px 0", textAlign: "center" }}>No static lists yet. Create one in Lists.</div>
              ) : (
                availableLists.map(l => (
                  <button key={l.id} onClick={() => addToList(l.id, l.name)} disabled={actionBusy}
                    style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 12px", marginBottom: 4, fontSize: 12, color: T.txt, background: "rgba(255,255,255,0.04)", border: "1px solid " + T.bdr, borderRadius: 6, cursor: actionBusy ? "default" : "pointer", opacity: actionBusy ? 0.6 : 1, fontFamily: "inherit" }}>
                    {l.name}
                  </button>
                ))
              )}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button onClick={() => setShowAddToListModal(false)} disabled={actionBusy} style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid " + T.bdr, background: "transparent", color: T.txt, fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ ADD TO CAMPAIGN MODAL ═══ */}
      {showAddToCampaignModal && (
        <div onClick={() => !actionBusy && setShowAddToCampaignModal(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()} style={{ width: 380, maxWidth: "92vw", maxHeight: "70vh", background: "#2C2E33", border: "1px solid " + T.bdr, borderRadius: 10, padding: 20, display: "flex", flexDirection: "column" }}>
            <div style={{ fontSize: 14, fontWeight: 400, color: T.txt, marginBottom: 4 }}>Add to Campaign</div>
            <div style={{ fontSize: 11, color: T.tx3, marginBottom: 14 }}>Pick a campaign to add {c.fn || "this contact"} to.</div>
            <div style={{ flex: 1, overflowY: "auto", marginBottom: 12 }}>
              {availableCampaigns === null ? (
                <div style={{ fontSize: 11, color: T.tx3, padding: "12px 0", textAlign: "center" }}>Loading campaigns...</div>
              ) : availableCampaigns.length === 0 ? (
                <div style={{ fontSize: 11, color: T.tx3, padding: "12px 0", textAlign: "center" }}>No active or draft campaigns. Create one in the Dialer.</div>
              ) : (
                availableCampaigns.map(camp => (
                  <button key={camp.id} onClick={() => addToCampaign(camp.id, camp.name)} disabled={actionBusy}
                    style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", textAlign: "left", padding: "10px 12px", marginBottom: 4, fontSize: 12, color: T.txt, background: "rgba(255,255,255,0.04)", border: "1px solid " + T.bdr, borderRadius: 6, cursor: actionBusy ? "default" : "pointer", opacity: actionBusy ? 0.6 : 1, fontFamily: "inherit" }}>
                    <span>{camp.name}</span>
                    <span style={{ fontSize: 9, color: T.tx3, textTransform: "uppercase", letterSpacing: ".06em" }}>{camp.status}</span>
                  </button>
                ))
              )}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button onClick={() => setShowAddToCampaignModal(false)} disabled={actionBusy} style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid " + T.bdr, background: "transparent", color: T.txt, fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ DELETE CONTACT CONFIRMATION ═══ */}
      {showDeleteModal && (
        <div onClick={() => !actionBusy && setShowDeleteModal(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()} style={{ width: 420, maxWidth: "92vw", background: "#2C2E33", border: "1px solid " + T.bdr, borderRadius: 10, padding: 24 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <div style={{ width: 28, height: 28, borderRadius: 8, background: "rgba(229,57,53,0.12)", display: "flex", alignItems: "center", justifyContent: "center", color: "#E53935", fontSize: 14, fontWeight: 500 }}>!</div>
              <div style={{ fontSize: 15, fontWeight: 400, color: T.txt }}>Delete {c.fn || ""} {c.ln || "contact"}?</div>
            </div>
            <div style={{ fontSize: 12, color: T.tx2, lineHeight: 1.6, marginBottom: 16 }}>
              This permanently removes the contact and all related activities. This cannot be undone.
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button onClick={() => setShowDeleteModal(false)} disabled={actionBusy} style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid " + T.bdr, background: "transparent", color: T.txt, fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
              <button onClick={confirmDeleteContact} disabled={actionBusy} style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid #E53935", background: "#E53935", color: "#FAFAFA", fontSize: 11, cursor: actionBusy ? "default" : "pointer", fontFamily: "inherit", opacity: actionBusy ? 0.6 : 1 }}>{actionBusy ? "Deleting..." : "Delete"}</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ CALL MODAL — Real Twilio Integration ═══ */}
      {showCallModal && (() => {
        const formatTime = (s) => {
          const m = Math.floor(s / 60);
          const sec = s % 60;
          return String(m).padStart(2, "0") + ":" + String(sec).padStart(2, "0");
        };

        const startRealCall = async () => {
          if (!c.ph) return;
          setCallActive(true);
          setCallTimer(0);

          // Start timer
          const interval = setInterval(() => {
            setCallTimer(prev => prev + 1);
          }, 1000);
          window._callInterval = interval;

          // Make real Twilio call
          try {
            const result = await makeCall(c.ph);
            if (!result || result.error) {
              console.warn("[ContactRecord] Call failed:", result?.error || "unknown");
              // Still keep the timer running as manual fallback
            } else {
              console.log("[ContactRecord] Twilio call initiated to:", c.ph);
            }
          } catch (e) {
            console.warn("[ContactRecord] Call error:", e.message);
          }
        };

        const endRealCall = () => {
          setCallActive(false);
          setCallEnded(true);
          clearInterval(window._callInterval);

          // Hang up the real Twilio call
          try { hangUp(); } catch (e) { console.warn("[ContactRecord] Hangup error:", e.message); }

          const secs = callTimer;
          if (secs < 60) setCallDuration(secs + " sec");
          else if (secs < 3600) setCallDuration(Math.floor(secs / 60) + " min");
          else setCallDuration(Math.floor(secs / 3600) + " hr " + Math.floor((secs % 3600) / 60) + " min");
        };

        return (
          <div style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 1000,
          }}>
            <div style={{
              width: 420, background: "#2C2E33", border: "1.5px solid rgba(217,219,221,0.12)",
              borderRadius: 8, overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
            }}>
              {/* Contact header */}
              <div style={{ padding: "20px 24px", background: callActive ? "#2E8B57" + "08" : T.sf, borderBottom: "1px solid " + T.bdr, textAlign: "center" }}>
                <div style={{ fontSize: 18, fontWeight: 400, color: T.txt, marginTop: 10 }}>{c.fn} {c.ln}</div>
                <div style={{ fontSize: 12, color: T.txt, marginTop: 2 }}>{c.title} - {c.co}</div>
                <div style={{ fontSize: 14, color: "#FAFAFA", marginTop: 6, fontWeight: 400 }}>{c.ph || "No phone"}</div>

                {/* Timer */}
                {callActive && (
                  <div style={{ fontSize: 32, fontWeight: 400, color: "#2E8B57", marginTop: 12, fontFamily: "inherit", letterSpacing: ".1em" }}>
                    {formatTime(callTimer)}
                  </div>
                )}
                {callActive && (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 6 }}>
                    <div style={{ width: 8, height: 8, borderRadius: 4, background: "#2E8B57", animation: "pulse 1.5s infinite" }} />
                    <span style={{ fontSize: 11, color: "#2E8B57", fontWeight: 400 }}>Call in progress</span>
                  </div>
                )}
              </div>

              {/* Pre-call: Start button */}
              {!callActive && !callEnded && (
                <div style={{ padding: "24px", textAlign: "center" }}>
                  {!c.ph ? (
                    <div style={{ fontSize: 13, color: T.txt, marginBottom: 12 }}>No phone number on record</div>
                  ) : (
                    <button onClick={startRealCall} style={{
                      width: 70, height: 70, borderRadius: 35, border: "none",
                      background: "#2E8B57", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                      margin: "0 auto", boxShadow: "0 4px 20px " + "#2E8B57" + "40",
                      transition: "transform 0.15s",
                    }}
                      onMouseEnter={e => e.currentTarget.style.transform = "scale(1.1)"}
                      onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}>
                      <Icon name="phone" size={28} color="#fff" />
                    </button>
                  )}
                  <div style={{ fontSize: 12, color: T.txt, marginTop: 10 }}>{c.ph ? "Click to call" : ""}</div>
                  <button onClick={() => setShowCallModal(false)} style={{
                    background: "none", border: "none", color: T.txt, fontSize: 11, cursor: "pointer", marginTop: 12,
                  }}>Cancel</button>
                </div>
              )}

              {/* Active call: End button */}
              {callActive && !callEnded && (
                <div style={{ padding: "24px", textAlign: "center" }}>
                  <button onClick={endRealCall} style={{
                    width: 70, height: 70, borderRadius: 35, border: "none",
                    background: "#E53935", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                    margin: "0 auto", boxShadow: "0 4px 20px " + "#E53935" + "40",
                    transition: "transform 0.15s",
                  }}
                    onMouseEnter={e => e.currentTarget.style.transform = "scale(1.1)"}
                    onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}>
                    <Icon name="x" size={20} color="#fff" />
                  </button>
                  <div style={{ fontSize: 12, color: T.txt, marginTop: 10 }}>End call</div>
                </div>
              )}

              {/* Post-call: Log form */}
              {callEnded && (
                <div style={{ padding: "16px 24px 24px" }}>
                  <div style={{ fontSize: 11, fontWeight: 400, color: "#2E8B57", marginBottom: 10, letterSpacing: ".06em" }}>LOG CALL DETAILS</div>
                  <div style={{ fontSize: 10, color: T.txt, marginBottom: 8 }}>Duration: <span style={{ color: T.txt, fontWeight: 400 }}>{callDuration}</span></div>

                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 10, color: T.txt, marginBottom: 4 }}>Outcome:</div>
                    <div style={{ display: "flex", gap: 4 }}>
                      {["Connected", "Voicemail", "No Answer", "Busy"].map(o => (
                        <button key={o} onClick={() => setCallOutcome(o)} style={{
                          flex: 1, padding: "6px 4px", borderRadius: 4, fontSize: 10, fontWeight: 400, cursor: "pointer",
                          border: callOutcome === o ? "2px solid " + "#2E8B57" : "1px solid " + T.bdr,
                          background: callOutcome === o ? "#2E8B57" + "12" : T.sf,
                          color: callOutcome === o ? "#2E8B57" : T.tx3,
                        }}>{o}</button>
                      ))}
                    </div>
                  </div>

                  <textarea value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Call notes..." autoFocus
                    style={{
                      width: "100%", background: "#2C2E33", border: "1px solid rgba(255,255,255,0.60)", borderRadius: 4,
                      padding: "8px 10px", color: T.txt, fontSize: 12, outline: "none", fontFamily: "inherit",
                      resize: "none", height: 70, boxSizing: "border-box",
                    }} />

                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
                    <button onClick={() => { setShowCallModal(false); setNoteText(""); }} style={{ background: "none", border: "none", color: T.txt, fontSize: 11, cursor: "pointer" }}>Skip</button>
                    <Btn primary style={{ fontSize: 11 }} onClick={() => {
                      // Write to activities table (for contact timeline)
                      store.logCall({
                        contactId: c.id,
                        title: "Call - " + callOutcome,
                        notes: noteText || "Call with " + c.fn + " " + c.ln,
                        duration: callDuration,
                      });
                      // Write to call_logs table (for Call Logs page)
                      const statusMap = { Connected: "completed", Voicemail: "voicemail", "No Answer": "no_answer", Busy: "busy" };
                      logCallToDb({
                        contactId: c.id,
                        userId: store.currentUserId || auth.currentUser?._dbId,
                        orgId: auth.currentUser?._orgId,
                        phoneNumber: c.ph || "",
                        duration: callTimer || 0,
                        status: statusMap[callOutcome] || "completed",
                        outcome: callOutcome,
                        notes: noteText || "",
                      });
                      setShowCallModal(false);
                      setNoteText("");
                    }}>Save & Close</Btn>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}
      </div>

      <EditActivityModal
        activity={editingActivity}
        onSave={async (updates) => editingActivity ? await store.updateActivity(editingActivity.id, updates) : false}
        onDelete={async () => editingActivity ? await store.deleteActivity(editingActivity.id) : false}
        onClose={() => setEditingActivity(null)}
      />
    </div>
  );
}
